import type { ThemeAIResultDistributionItem, ThemeAITheme } from '../types/gotypes'
import type { ThemeDistributionItem } from '../components/theme-ai-tasks/ThemeDistribution'

/**
 * Joins a run's theme distribution with the code frame it was coded against,
 * so every theme in the frame appears — a theme no response mentioned shows
 * as 0% instead of disappearing, which is exactly the signal a user needs to
 * merge or drop it (spec #916 §5.6). Sorted by share, then frame order.
 *
 * A code in the distribution that is not in the frame (the frame changed
 * under an older run) is kept, labelled with the name the server resolved.
 */
export function buildThemeDistribution(
  distribution: ThemeAIResultDistributionItem[],
  frameThemes: ThemeAITheme[] | undefined,
): ThemeDistributionItem[] {
  const byCode = new Map<string, ThemeDistributionItem>()
  const order = new Map<string, number>()

  for (const theme of frameThemes || []) {
    const code = String(theme.code)
    byCode.set(code, { code, name: theme.name, count: 0, pct: 0 })
    order.set(code, theme.order ?? theme.code)
  }
  for (const item of distribution) {
    const code = item.code || item.id || item.name
    const existing = byCode.get(code)
    byCode.set(code, { code, name: existing?.name || item.name, count: item.count, pct: item.pct })
  }

  return [...byCode.values()].sort((a, b) => {
    if (b.pct !== a.pct) return b.pct - a.pct
    return (order.get(a.code) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.code) ?? Number.MAX_SAFE_INTEGER)
  })
}

/** Recommended preview size by pool size (spec #916 §5.6), never above the pool. */
export function recommendedSampleSize(poolSize: number): number {
  const tier = poolSize < 500 ? 50 : poolSize <= 5000 ? 100 : 200
  return Math.max(1, Math.min(tier, poolSize))
}

/** The sample sizes worth offering for a pool: the tiers below it, plus "all" when the pool is small. */
export function sampleSizeOptions(poolSize: number): number[] {
  const options = [50, 100, 200].filter((size) => size < poolSize)
  if (poolSize > 0 && poolSize <= 200) options.push(poolSize)
  return options.length > 0 ? options : [Math.max(poolSize, 1)]
}

/** A typed or slid sample size, made whole and kept between 1 and the pool. */
export function clampSampleSize(value: number, poolSize: number): number {
  const max = Math.max(1, Math.floor(poolSize))
  if (!Number.isFinite(value) || value < 1) return 1
  return Math.min(Math.round(value), max)
}
